import { memo } from "react";
import { ButtonBase, IconButton, Popover } from "@mui/material";
import { ChevronLeftIcon, ChevronRightIcon } from "components/icons";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import dayjs from "dayjs";

const WEEKDAY_INITIALS = ["S", "M", "T", "W", "T", "F", "S"];

// A CSS variable rather than theme.borders.*: this component renders without the app's
// ThemeProvider. It follows the base colour and light/dark mode. @since NEXT_VERSION
const FIELD_BORDER = "var(--border)";

const CELL_HEIGHT = 34;
const DAYS_PER_WEEK = 7;

/**
 * Day picker for one row of the bulk entry table.
 *
 * A day another row already carries stays fully selectable: the schema holds several activities per
 * owner per day, and a different description on the same day is the case this feature exists for.
 * The only day-wide rule is the 24-hour roll-up, shown as a badge here and enforced by the
 * validator, not by hiding dates.
 *
 * @since NEXT_VERSION
 */
const RowDatePicker = ({
  open,
  anchorEl,
  month,
  value,
  savedHoursByDate = {},
  datesUsedByOtherRows = [],
  minDate,
  maxDate,
  onPick,
  onClose,
  onMonthChange,
}) => {
  const monthStart = (month ?? dayjs()).startOf("month");
  // Only this month's days are offered, so the cells before the 1st stand empty and the grid is
  // exactly as many weeks as the month occupies — four, five or six. Reserving a sixth week for
  // every month would leave a blank row below the short ones. @since NEXT_VERSION
  const leadingBlanks = monthStart.day();
  const daysInMonth = monthStart.daysInMonth();
  const weeksInMonth = Math.ceil((leadingBlanks + daysInMonth) / DAYS_PER_WEEK);
  const usedByOthers = new Set(datesUsedByOtherRows);

  const cellFor = (date) => {
    const iso = date.format("YYYY-MM-DD");
    // @since NEXT_VERSION — the window is bounded at both ends now, so one flag covers both.
    const beyondWindow =
      (Boolean(maxDate) && date.isAfter(maxDate, "day")) ||
      (Boolean(minDate) && date.isBefore(minDate, "day"));
    const isWeekend = date.day() === 0 || date.day() === 6;
    const saved = Number(savedHoursByDate[iso]) || 0;
    const selected = iso === value;

    const badge = saved > 0 ? `${saved}h` : "";

    let title = "";
    if (beyondWindow) title = "Beyond the allowed window";
    else if (saved > 0) title = `${saved} h already saved`;
    else if (usedByOthers.has(iso)) title = "Another row already uses this day — that is allowed";

    return (
      // ButtonBase rather than MDBox component="button": MDBox does not forward `component`, so the
      // cells rendered as divs with no button role — unreachable by keyboard and by assistive tech.
      <ButtonBase
        key={iso}
        disableRipple
        disabled={beyondWindow}
        onClick={() => onPick(iso)}
        title={title}
        sx={{
          height: CELL_HEIGHT,
          border: 0,
          // Literal rather than theme.borders.borderRadius.md: this suite renders without the
          // app's ThemeProvider, so the custom theme keys are undefined here. 6px is that token.
          borderRadius: "6px",
          cursor: beyondWindow ? "default" : "pointer",
          font: "inherit",
          fontSize: 12,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1px",
          p: 0,
          backgroundColor: selected ? "info.main" : "transparent",
          color: selected
            ? "info.contrastText"
            : beyondWindow
            ? "grey.400"
            : isWeekend
            ? "grey.500"
            : "grey.800",
          fontWeight: selected ? 700 : 400,
          "&:hover": {
            backgroundColor: selected ? "info.focus" : beyondWindow ? "transparent" : "grey.200",
          },
        }}
      >
        <span>{date.date()}</span>
        {badge && (
          <MDBox
            component="span"
            sx={{
              fontSize: 8,
              fontWeight: 700,
              lineHeight: 1,
              color: selected ? "info.contrastText" : "grey.500",
              opacity: selected ? 0.75 : 1,
            }}
          >
            {badge}
          </MDBox>
        )}
      </ButtonBase>
    );
  };

  return (
    <Popover
      open={Boolean(open)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      slotProps={{
        // The theme pads the popover surface, which drew a second white ring around the calendar
        // and read as a panel sitting behind it. The surface is the calendar: no padding of its
        // own, one visible edge, and a shadow that lifts it off the row underneath.
        // @since NEXT_VERSION
        paper: {
          sx: {
            p: 0,
            backgroundColor: "var(--surface)",
            border: `1px solid ${FIELD_BORDER}`,
            borderRadius: "8px",
            boxShadow: "var(--shadow-2)",
          },
        },
      }}
    >
      <MDBox sx={{ width: 248, p: 1.25 }}>
        <MDBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <IconButton
            size="small"
            aria-label="Previous month"
            onClick={() => onMonthChange(monthStart.subtract(1, "month"))}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <MDTypography variant="button" fontWeight="bold" color="dark" sx={{ fontSize: 13 }}>
            {monthStart.format("MMMM YYYY")}
          </MDTypography>
          <IconButton
            size="small"
            aria-label="Next month"
            onClick={() => onMonthChange(monthStart.add(1, "month"))}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </MDBox>

        <MDBox sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: "2px", mb: "3px" }}>
          {WEEKDAY_INITIALS.map((initial, index) => (
            <MDTypography
              key={`dow-${index}`}
              variant="caption"
              fontWeight="bold"
              color="secondary"
              textAlign="center"
              sx={{ fontSize: 10 }}
            >
              {initial}
            </MDTypography>
          ))}
        </MDBox>

        <MDBox
          data-testid="picker-days"
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            gridTemplateRows: `repeat(${weeksInMonth}, ${CELL_HEIGHT}px)`,
            gap: "2px",
          }}
        >
          {Array.from({ length: leadingBlanks }, (unused, index) => (
            <MDBox key={`blank-${index}`} data-testid="picker-blank" sx={{ height: CELL_HEIGHT }} />
          ))}
          {Array.from({ length: daysInMonth }, (unused, index) =>
            cellFor(monthStart.date(index + 1))
          )}
        </MDBox>
      </MDBox>
    </Popover>
  );
};

export default memo(RowDatePicker);
